import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { applyQuillFontSizeStep, ensureQuillFontSizesRegistered } from '../utils/quillFontSize';

ensureQuillFontSizesRegistered();

export type BugReportSubmitPayload = {
  title: string;
  category: string;
  html: string;
};

type Props = {
  open: boolean;
  isDarkMode: boolean;
  busy?: boolean;
  error?: string | null;
  success?: string | null;
  onClose: () => void;
  onSubmit: (payload: BugReportSubmitPayload) => Promise<void>;
};

const CATEGORIES = ['Bug', 'UI/UX', 'Performance', 'Data issue', 'Feature request', 'Other'] as const;

function stripHtml(html: string): string {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const BugReportModal: React.FC<Props> = ({
  open,
  isDarkMode,
  busy = false,
  error = null,
  success = null,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Bug');
  const [html, setHtml] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const quillRef = useRef<ReactQuill | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const lastDetectedDirectionRef = useRef<'rtl' | 'ltr'>('ltr');
  const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState<DOMRect | null>(null);

  useEscapeKey(Boolean(open), () => {
    if (!busy) onClose();
  });

  const colors = useMemo(() => {
    const dark = isDarkMode;
    return {
      overlay: dark ? 'rgba(2,6,23,0.72)' : 'rgba(15,23,42,0.45)',
      card: dark ? '#0B1220' : '#FFFFFF',
      border: dark ? '#334155' : '#E5E7EB',
      text: dark ? '#E5E7EB' : '#111827',
      muted: dark ? '#94A3B8' : '#6B7280',
      inputBg: dark ? '#111827' : '#FFFFFF',
      primary: '#2F58F2',
      secondaryBg: dark ? '#111827' : '#F3F4F6',
      secondaryText: dark ? '#E5E7EB' : '#111827',
      danger: '#DC2626',
      ok: '#166534',
    };
  }, [isDarkMode]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: '#bug-report-toolbar',
      handlers: {
        undo: () => {
          const editor = quillRef.current?.getEditor?.() as any;
          editor?.history?.undo?.();
        },
        redo: () => {
          const editor = quillRef.current?.getEditor?.() as any;
          editor?.history?.redo?.();
        },
        image: () => {
          const editor = quillRef.current?.getEditor?.();
          if (!editor) return;

          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = () => {
            const file = input.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = () => {
              const src = String(reader.result || '').trim();
              if (!src) return;

              const range = editor.getSelection(true);
              const index = range && Number.isFinite(range.index) ? range.index : editor.getLength();
              editor.insertEmbed(index, 'image', src, 'user');
              editor.setSelection(index + 1, 0, 'silent');
            };
            reader.readAsDataURL(file);
          };
          input.click();
        },
      },
    },
    history: {
      delay: 800,
      maxStack: 100,
      userOnly: true,
    },
  }), []);

  const quillFormats = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'indent',
      'link',
      'image',
      'align',
      'color',
      'background',
      'size',
    ],
    []
  );

  const insertTextAtCursor = (text: string) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const range = editor.getSelection(true);
    const index = range && Number.isFinite(range.index) ? range.index : editor.getLength();
    editor.insertText(index, text, 'user');
    editor.setSelection(index + text.length, 0, 'silent');
    editor.focus();
  };

  const openEmojiMenu = (target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    setEmojiAnchor(rect);
    setOpenEmojiPicker(true);
  };

  const applyEmoji = (emoji: string) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection?.(true) || { index: editor.getLength?.() || 0, length: 0 };
    editor.insertText(range.index, emoji, 'user');
    editor.setSelection(range.index + emoji.length, 0, 'silent');
    setOpenEmojiPicker(false);
  };

  useEffect(() => {
    if (!openEmojiPicker) return;

    const onMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;
      const emojiPopover = document.getElementById('bug-report-emoji-popover');
      if (emojiPopover && emojiPopover.contains(target)) return;
      setOpenEmojiPicker(false);
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOpenEmojiPicker(false);
      }
    };

    const onScrollOrResize = () => {
      setOpenEmojiPicker(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [openEmojiPicker]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const input = titleInputRef.current;
      if (!input || busy) return;
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [open, busy]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const stopNativeBubble = (ev: Event) => {
      ev.stopPropagation();
    };

    dialog.addEventListener('mousedown', stopNativeBubble);
    dialog.addEventListener('click', stopNativeBubble);
    dialog.addEventListener('pointerdown', stopNativeBubble);

    return () => {
      dialog.removeEventListener('mousedown', stopNativeBubble);
      dialog.removeEventListener('click', stopNativeBubble);
      dialog.removeEventListener('pointerdown', stopNativeBubble);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const editor = quillRef.current?.getEditor?.() as any;
    if (!editor) return;

    const editorElement = editor.root as HTMLElement;
    const containerElement = editorElement.closest('.ql-container') as HTMLElement | null;

    const isRTLChar = (ch: string) => /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(ch);
    const isLTRChar = (ch: string) => /[A-Za-z]/.test(ch);

    const applyDirection = (dir: 'rtl' | 'ltr') => {
      lastDetectedDirectionRef.current = dir;
      editorElement.style.direction = dir;
      editorElement.style.textAlign = dir === 'rtl' ? 'right' : 'left';
      editorElement.setAttribute('dir', dir);

      if (containerElement) {
        containerElement.style.direction = dir;
        containerElement.setAttribute('dir', dir);
      }

      try {
        editor.format('direction', dir, 'silent');
        editor.format('align', dir === 'rtl' ? 'right' : 'left', 'silent');
        const len = typeof editor.getLength === 'function' ? Math.max(editor.getLength(), 1) : 1;
        editor.formatLine(0, len, { direction: dir, align: dir === 'rtl' ? 'right' : 'left' }, 'silent');
      } catch {
        // Ignore formatting failures while editor initializes.
      }
    };

    const applyDirectionFromText = (rawText: string) => {
      const text = String(rawText ?? '');
      const firstNonWs = text.replace(/\s+/g, '').charAt(0);
      if (!firstNonWs) {
        applyDirection(lastDetectedDirectionRef.current);
        return;
      }
      if (isRTLChar(firstNonWs)) applyDirection('rtl');
      else applyDirection('ltr');
    };

    const onTextChange = () => {
      applyDirectionFromText(editor.getText?.() || '');
    };

    const onSelectionChange = (range: any) => {
      // Ignore blur events; otherwise Quill formatting can steal focus back from the Title input.
      if (!range) return;
      if (typeof editor.getLength === 'function' && editor.getLength() <= 1) {
        applyDirection(lastDetectedDirectionRef.current);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const key = (e as any).key as string | undefined;
      if (!key || key.length !== 1) return;

      const currentText = String(editor.getText?.() || '').trim();
      if (currentText.length !== 0) return;

      if (isRTLChar(key)) applyDirection('rtl');
      else if (isLTRChar(key)) applyDirection('ltr');
    };

    const onBeforeInput = (e: Event) => {
      const ie = e as InputEvent;
      const data = typeof ie.data === 'string' ? ie.data : '';
      if (!data) return;

      const currentText = String(editor.getText?.() || '').trim();
      if (currentText.length !== 0) return;

      const ch = data.charAt(0);
      if (isRTLChar(ch)) applyDirection('rtl');
      else if (isLTRChar(ch)) applyDirection('ltr');
    };

    editor.on?.('text-change', onTextChange);
    editor.on?.('selection-change', onSelectionChange);
    editorElement.addEventListener('beforeinput', onBeforeInput as any, true);
    editorElement.addEventListener('keydown', onKeyDown, true);

    requestAnimationFrame(() => {
      applyDirectionFromText(editor.getText?.() || '');
    });

    return () => {
      editor.off?.('text-change', onTextChange);
      editor.off?.('selection-change', onSelectionChange);
      editorElement.removeEventListener('beforeinput', onBeforeInput as any, true);
      editorElement.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, html]);

  const resetDraft = () => {
    setTitle('');
    setCategory('Bug');
    setHtml('');
    setValidationError(null);
  };

  const handleClose = () => {
    if (busy) return;
    resetDraft();
    onClose();
  };

  const handleSubmit = async () => {
    setValidationError(null);

    const safeTitle = title.trim();
    const plainText = stripHtml(html);
    if (!safeTitle) {
      setValidationError('Please enter a short title.');
      return;
    }
    if (!plainText) {
      setValidationError('Please describe the issue before sending.');
      return;
    }

    await onSubmit({
      title: safeTitle,
      category,
      html,
    });

    resetDraft();
  };

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        background: colors.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Report issue"
        data-bug-report-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(920px, 96vw)',
          minHeight: 'min(640px, 88vh)',
          maxHeight: '88vh',
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(15,23,42,0.35)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px 10px 16px',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '100%',
                color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
              }}
            >
              Report Bug / Feedback
            </div>
            <div style={{ fontSize: 12, color: colors.muted, marginTop: 3 }}>
              Share issue details, screenshots and steps to reproduce.
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.muted,
              fontSize: 24,
              lineHeight: 1,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <style>{`
          .bug-report-modal-quill .quill {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .bug-report-modal-quill .ql-toolbar {
            order: 2;
            border: none !important;
            padding: 8px 0 !important;
            display: flex !important;
            gap: 8px !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            flex-shrink: 0 !important;
            min-height: 40px !important;
            background: ${isDarkMode ? '#111827' : 'white'} !important;
            margin-top: 8px !important;
          }
          .bug-report-modal-quill .ql-container {
            order: 1;
            flex: 1;
            background-color: ${isDarkMode ? '#111827' : '#ffffff'};
            border: none !important;
            overflow: hidden;
          }
          .bug-report-modal-quill .ql-editor {
            border-radius: 6px;
            min-height: 220px;
            max-height: 320px;
            overflow-y: auto;
            direction: auto;
            text-align: start;
            font-size: 16px;
            color: ${isDarkMode ? '#D1D5DB' : '#0D0D0D'};
          }
          .bug-report-modal-quill .ql-editor img {
            display: block;
            max-width: 100%;
            height: auto;
            margin: 8px 0;
          }
          .bug-report-modal-quill .ql-editor.ql-blank::before {
            color: ${isDarkMode ? '#D1D5DB' : '#9CA3AF'};
          }
          .bug-report-modal-quill .ql-editor.ql-blank {
            background-color: ${isDarkMode ? '#111827' : 'transparent'};
          }
          .bug-report-modal-quill .ql-editor p {
            direction: auto;
            text-align: start;
          }
          .bug-report-modal-quill .ql-toolbar .ql-formats {
            margin-right: 0 !important;
            display: inline-flex !important;
            gap: 22px !important;
          }
          #bug-report-toolbar button,
          #bug-report-toolbar .ql-picker {
            width: 28px !important;
            height: 28px !important;
            padding: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          #bug-report-toolbar .ql-picker-label {
            padding: 0 !important;
            width: 24px !important;
            height: 24px !important;
          }
          #bug-report-toolbar .ql-align .ql-picker-label {
            width: 32px !important;
            height: 32px !important;
            padding: 0 !important;
          }
          #bug-report-toolbar .ql-align .ql-picker-label svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-align .ql-picker-label::before {
            content: '' !important;
            display: block !important;
            width: 28px !important;
            height: 28px !important;
            background-image: url('/images/align.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-undo img,
          #bug-report-toolbar .ql-redo img {
            width: 18px !important;
            height: 18px !important;
          }
          #bug-report-toolbar button img {
            width: 18px !important;
            height: 18px !important;
          }
          #bug-report-toolbar .ql-fontDecrease img,
          #bug-report-toolbar .ql-fontIncrease img {
            width: 24px !important;
            height: 24px !important;
          }
          #bug-report-toolbar .ql-picker-label::before {
            width: 16px !important;
            height: 16px !important;
          }
          .bug-report-modal-quill .ql-toolbar .ql-picker-options {
            max-height: 200px !important;
            overflow-y: auto !important;
          }
          .bug-report-modal-quill .ql-toolbar button {
            width: 28px !important;
            height: 28px !important;
            padding: 4px !important;
          }
          .bug-report-modal-quill .ql-toolbar button svg {
            width: 18px;
            height: 18px;
          }
          #bug-report-toolbar .ql-mention {
            font-size: 0 !important;
            padding: 0 !important;
          }
          #bug-report-toolbar .ql-mention::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/mention.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-emoji {
            font-size: 16px;
          }
          #bug-report-toolbar .ql-color .ql-picker-label,
          #bug-report-toolbar .ql-background .ql-picker-label {
            padding: 0 !important;
            width: 28px !important;
            height: 28px !important;
          }
          #bug-report-toolbar .ql-color .ql-picker-label svg,
          #bug-report-toolbar .ql-background .ql-picker-label svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-color .ql-picker-label::before {
            content: '' !important;
            display: block !important;
            width: 20px !important;
            height: 20px !important;
            background-image: url('/images/font-color.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
            margin: 4px !important;
          }
          #bug-report-toolbar .ql-background .ql-picker-label::before {
            content: '' !important;
            display: block !important;
            width: 20px !important;
            height: 20px !important;
            background-image: url('/images/edit-editor.svg?v=20260303') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
            margin: 4px !important;
          }
          #bug-report-toolbar .ql-link svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-link::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/insert_link.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-list svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-list::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/list_check_line.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-indent svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-indent[value='-1']::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/list-indent-increase.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-indent[value='+1']::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/list-indent-decrease.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-bold svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-bold::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/bold_line.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-italic svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-italic::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/italic_line.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-underline svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-underline::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/underline_line.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          #bug-report-toolbar .ql-strike svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-strike::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/strikethrough_line.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          .bug-report-modal-quill .ql-tooltip {
            position: absolute !important;
            z-index: 10004 !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            bottom: 50px !important;
          }
          .bug-report-modal-quill .ql-tooltip.ql-editing {
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
          #bug-report-toolbar .ql-emoji {
            font-size: 0 !important;
            padding: 0 !important;
          }
          #bug-report-toolbar .ql-emoji::before {
            content: '' !important;
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            background-image: url('/images/smile.svg') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
            font-size: 16px !important;
          }
          #bug-report-toolbar .ql-image svg {
            display: none !important;
          }
          #bug-report-toolbar .ql-image {
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            min-height: 24px !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          #bug-report-toolbar .ql-image::before {
            content: '' !important;
            display: block !important;
            width: 24px !important;
            height: 24px !important;
            margin-top: 6px !important;
            background-image: url('/images/insertimage.svg?v=20260303') !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
        `}</style>

        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="bug-report-title" style={{ fontSize: 12, fontWeight: 700, color: colors.muted, display: 'block', marginBottom: 4 }}>
                Title
              </label>
              <input
                id="bug-report-title"
                ref={titleInputRef}
                autoFocus
                type="text"
                value={title}
                onPointerDownCapture={(e) => {
                  e.stopPropagation();
                  titleInputRef.current?.focus();
                }}
                onClick={() => {
                  titleInputRef.current?.focus();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDownCapture={(e) => e.stopPropagation()}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Example: Login popup fails after refresh"
                maxLength={256}
                style={{
                  width: '100%',
                  height: 38,
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: colors.inputBg,
                  color: colors.text,
                  padding: '0 10px',
                  boxSizing: 'border-box',
                  pointerEvents: 'auto',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>

            <div>
              <label htmlFor="bug-report-category" style={{ fontSize: 12, fontWeight: 700, color: colors.muted, display: 'block', marginBottom: 4 }}>
                Category
              </label>
              <select
                id="bug-report-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as (typeof CATEGORIES)[number])}
                disabled={busy}
                style={{
                  width: '100%',
                  height: 38,
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: colors.inputBg,
                  color: colors.text,
                  padding: '0 10px',
                  boxSizing: 'border-box',
                }}
              >
                {CATEGORIES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            id="note-content-wrapper"
            style={{
              border: `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
              borderRadius: '6px',
              padding: '12px',
              paddingBottom: '0px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              gap: '6px',
              overflow: 'visible',
              minHeight: 350,
            }}
          >
            <div id="note-editor-content" style={{ flex: 1, overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
              <div className="bug-report-modal-quill" style={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={html}
                    onChange={setHtml}
                    placeholder="Write steps, expected behavior, actual behavior. You can paste or upload images."
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={busy}
                    style={{ height: '100%', border: 'none' }}
                  />
                </div>
              </div>
            </div>

            <div
              id="bug-report-toolbar"
              style={{
                paddingTop: '18px',
                paddingBottom: '12px',
                marginTop: '0px',
                display: 'flex',
                gap: '0px',
                flexWrap: 'nowrap',
                background: isDarkMode ? '#111827' : 'white',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                borderBottom: '1px solid #ECECF4',
                marginBottom: '2px',
              }}
            >
              <span className="ql-formats">
                <button className="ql-undo" title="Undo">
                  <img src="/images/undo-icon.svg" alt="Undo" style={{ width: '18px', height: '18px' }} />
                </button>
                <button className="ql-redo" title="Redo">
                  <img
                    src="/images/undo-icon.svg"
                    alt="Redo"
                    style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                  />
                </button>
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#D0D0D0',
                  margin: '0 8px',
                  flexShrink: 0,
                }}
              ></span>
              <span className="ql-formats">
                <button className="ql-list" value="ordered" title="Ordered List"></button>
                <button className="ql-list" value="bullet" title="Bullet List"></button>
              </span>
              <span className="ql-formats">
                <select className="ql-align" title="Text Alignment"></select>
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#D0D0D0',
                  margin: '0 8px',
                  flexShrink: 0,
                }}
              ></span>
              <span className="ql-formats">
                <button className="ql-indent" value="+1" title="Indent Increase"></button>
                <button className="ql-indent" value="-1" title="Indent Decrease"></button>
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#D0D0D0',
                  margin: '0 8px',
                  flexShrink: 0,
                }}
              ></span>
              <span className="ql-formats">
                <select className="ql-color" title="Text Color"></select>
                <select className="ql-background" title="Background Color"></select>
              </span>
              <span className="ql-formats">
                <button
                  className="ql-fontDecrease"
                  title="Decrease Font"
                  onClick={(e) => {
                    e.preventDefault();
                    const quill = quillRef.current?.getEditor?.();
                    applyQuillFontSizeStep(quill, -1);
                  }}
                  style={{ marginRight: '6px' }}
                >
                  <img src="/images/a-arrow-down.svg" alt="A-" style={{ width: '24px', height: '24px' }} />
                </button>
                <button
                  className="ql-fontIncrease"
                  title="Increase Font"
                  onClick={(e) => {
                    e.preventDefault();
                    const quill = quillRef.current?.getEditor?.();
                    applyQuillFontSizeStep(quill, 1);
                  }}
                >
                  <img src="/images/a-arrow-up.svg" alt="A+" style={{ width: '24px', height: '24px' }} />
                </button>
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#D0D0D0',
                  margin: '0 8px',
                  flexShrink: 0,
                }}
              ></span>
              <span className="ql-formats">
                <button className="ql-bold" title="Bold"></button>
                <button className="ql-italic" title="Italic"></button>
                <button className="ql-underline" title="Underline"></button>
                <button className="ql-strike" title="Strike"></button>
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#D0D0D0',
                  margin: '0 8px',
                  flexShrink: 0,
                }}
              ></span>
              <span className="ql-formats">
                <button className="ql-link" title="Insert Link"></button>
                <button
                  className="ql-mention"
                  title="Mention (@)"
                  onClick={(e) => {
                    e.preventDefault();
                    insertTextAtCursor('@');
                  }}
                >
                  @
                </button>
                <button
                  className="ql-emoji"
                  title="Emoji"
                  onClick={(e) => {
                    e.preventDefault();
                    openEmojiMenu(e.currentTarget);
                  }}
                >
                  😊
                </button>
                <button className="ql-image" title="Insert Image"></button>
              </span>
            </div>
          </div>

          {openEmojiPicker && emojiAnchor && (
            <div
              id="bug-report-emoji-popover"
              style={{
                position: 'fixed',
                top: emojiAnchor.bottom + 6,
                left: emojiAnchor.left - 24,
                zIndex: 10050,
                background: '#ffffff',
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                padding: 8,
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                width: 252,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, gridAutoRows: '24px' }}>
                {[
                  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
                  '😊', '😍', '😘', '😎', '🤔', '😴', '😮', '😇',
                  '👍', '👎', '👏', '🙌', '🙏', '🤝', '💪', '🔥',
                  '✨', '🎯', '📈', '📉', '💡', '📝', '✅', '❗',
                ].map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => applyEmoji(emoji)}
                    style={{
                      width: 24,
                      height: 24,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 18,
                      lineHeight: '24px',
                    }}
                    aria-label={`emoji-${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(validationError || error || success) && (
            <div style={{ fontSize: 12, color: validationError || error ? colors.danger : colors.ok, fontWeight: 700 }}>
              {validationError || error || success}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '12px 16px 16px 16px',
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.secondaryBg,
              color: colors.secondaryText,
              cursor: busy ? 'not-allowed' : 'pointer',
              fontWeight: 700,
            }}
          >
            Cancel
          </button>
          <button
            id="bug-report-submit"
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#000000',
              color: '#ffffff',
              border: '1px solid #ECECF4',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Sending…' : 'Send Report'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BugReportModal;
